/**
 * Sidebar — Navigation principale Averdy Agency (SaaS multi-tenant).
 *
 * Design: reproduit fidèlement le sidebar BE-TAYE (référence utilisateur).
 *   - Sections rétractables (chevron droit → 90°)
 *   - Item actif: dot bleu + fond bg-blue-50/80 + texte text-blue-700
 *   - Modules "Bientôt": lock icon + badge italique "Bientôt"
 *   - Header: bulle verte de statut + logo tenant + slogan
 *   - Footer: badge utilisateur (initiale) + role + logout
 *
 * Différences SaaS vs BE-TAYE:
 *   - Feature flags via session.features (chaque module a un flag)
 *   - Tenant switcher accessible via header (pas dans le sidebar)
 *   - Modules hors plan: affichés grisés "Bientôt" (lock)
 *   - Tenant name dans header au lieu de "BE-TAYE" en dur
 */
import { useState, useEffect } from "react";
import { useLocation, useNavigate, NavLink } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import HRSearchBar from "@/components/HRSearchBar";
import { APP_NAME, productLabel } from "@/lib/branding";
import { BrandLogo } from "@/components/BrandLogo";
import {
  LayoutDashboard, BarChart3, Users, FileText, AlertTriangle,
  LogOut, ChevronRight, FileSignature, Receipt, PieChart, Calendar,
  Package, Hash,
  Euro, BookOpen, Menu, Landmark, ShoppingBag, TrendingUp, Scale,
  Upload, UserCircle, Trash2, Car, Send, Zap, FolderArchive,
  Settings, CheckSquare, FileCheck, Ban, Sparkles, Activity,
  ShieldCheck, Lock, Plug, CreditCard, ScrollText, Inbox, HardDrive,
  Mail, KeyRound, ShieldAlert, SlidersHorizontal, Wallet, ClipboardList,
  History, FileSpreadsheet, RefreshCw, Banknote, Merge, UploadCloud, Repeat,
  Briefcase, Bell,
} from "lucide-react";

const IA_ITEM = { path: "/app/assistant", label: productLabel("IA"), icon: Sparkles, feature: "ai_copilot" };

// ============================================================================
// NAV DEFINITIONS — 1 groupe par métier, ordre identique BE-TAYE
// ============================================================================
const NAV_PILOTAGE = [
  { path: "/app", label: "Tableau de bord", icon: Zap, end: true },
  { path: "/app/pilotage", label: "Pilotage exécutif", icon: TrendingUp, feature: "invoicing" },
  { path: "/app/pilotage/board-pack", label: "Dossier CA (DAF)", icon: FileText, feature: "hr" },
  IA_ITEM,
];

const NAV_VENTES = [
  { path: "/app/sales", label: "Vue d'ensemble", icon: LayoutDashboard, feature: "invoicing", end: true },
  { path: "/app/invoices", label: "Factures", icon: FileText, feature: "invoicing" },
  { path: "/app/quotes", label: "Devis", icon: FileSignature, feature: "invoicing" },
  { path: "/app/clients", label: "Clients", icon: Users, feature: "invoicing" },
  { path: "/app/invoices/products", label: "Catalogue", icon: Package, feature: "invoicing" },
  { path: "/app/invoices/settings", label: "Paramètres", icon: SlidersHorizontal, feature: "invoicing" },
  IA_ITEM,
];

const NAV_ACHATS = [
  { path: "/app/purchases", label: "Vue d'ensemble", icon: LayoutDashboard, feature: "expenses", end: true },
  { path: "/app/purchases/supplier-invoices", label: "Factures fournisseurs", icon: FileText, feature: "expenses" },
  { path: "/app/suppliers", label: "Fournisseurs", icon: Users, feature: "expenses" },
  { path: "/app/expenses", label: "Notes de frais", icon: ClipboardList, feature: "expenses" },
  IA_ITEM,
];

const NAV_CONTRATS = [
  { path: "/app/contracts", label: "Vue d'ensemble", icon: LayoutDashboard, feature: "contracts", end: true },
  { path: "/app/contracts/annuaire", label: "Contrats", icon: BookOpen, feature: "contracts" },
  { path: "/app/contracts/compare", label: "Comparer", icon: Scale, feature: "contracts" },
  { path: "/app/contracts/qualite", label: "Qualité & Risques", icon: ShieldCheck, feature: "contracts" },
  { path: "/app/contracts/parametres", label: "Paramètres", icon: Settings, feature: "contracts" },
  IA_ITEM,
];

const NAV_ENGAGEMENTS = [
  { path: "/app/depenses", label: "Vue d'ensemble", icon: LayoutDashboard, feature: "expenses", end: true },
  { path: "/app/depenses/contrats", label: "Contrats fournisseurs", icon: FileCheck, feature: "expenses" },
  { path: "/app/depenses/import", label: "Importer (OCR IA)", icon: Upload, feature: "expenses" },
  { path: "/app/depenses/calendrier", label: "Calendrier échéances", icon: Calendar, feature: "expenses" },
  { path: "/app/depenses/resiliations", label: "Résiliations", icon: Ban, feature: "expenses" },
  { path: "/app/depenses/parametres", label: "Paramètres", icon: Settings, feature: "expenses" },
  IA_ITEM,
];

const NAV_TREASURY = [
  { path: "/app/reconciliation", label: "Rapprochement", icon: Banknote, feature: "treasury" },
  { path: "/app/treasury", label: "Connexions bancaires", icon: Landmark, feature: "treasury" },
  IA_ITEM,
];

const NAV_RH = [
  { path: "/app/hr", label: "Tableau de bord RH", icon: BarChart3, feature: "hr", end: true },
  { path: "/app/hr/salaries", label: "Salariés", icon: Users, feature: "hr" },
  { path: "/app/hr/persons", label: "Mandataires & externes", icon: Briefcase, feature: "hr" },
  { path: "/app/hr/absences", label: "Absences & congés", icon: Calendar, feature: "hr" },
  { path: "/app/hr/advances", label: "Acomptes", icon: Wallet, feature: "hr" },
  { path: "/app/hr/advances-configs", label: "Configs acomptes", icon: SlidersHorizontal, feature: "hr" },
  { path: "/app/hr/payroll", label: "Variables paie", icon: Receipt, feature: "hr" },
  { path: "/app/hr/payslips", label: "Bulletins", icon: FileText, feature: "hr" },
  { path: "/app/hr/payslips/bulk-import", label: "Import massif bulletins", icon: UploadCloud, feature: "hr" },
  { path: "/app/hr/contracts-overview", label: "Contrats RH", icon: FileSignature, feature: "hr" },
  { path: "/app/hr/rup", label: "Registre du personnel", icon: BookOpen, feature: "hr" },
  { path: "/app/hr/alerts", label: "Alertes conformité", icon: ShieldAlert, feature: "hr" },
  { path: "/app/hr/trash", label: "Corbeille", icon: Trash2, feature: "hr" },
  { path: "/app/hr/settings", label: "Paramètres RH", icon: Settings, feature: "hr" },
  IA_ITEM,
];

const NAV_FLEET = [
  { path: "/app/fleet", label: "Tableau de bord", icon: BarChart3, feature: "fleet", end: true },
  { path: "/app/fleet/vehicles", label: "Véhicules", icon: Car, feature: "fleet" },
  { path: "/app/fleet/contracts", label: "Contrats", icon: FileSignature, feature: "fleet" },
  { path: "/app/fleet/costs-summary", label: "Budget & Coûts", icon: Wallet, feature: "fleet" },
  { path: "/app/fleet/claims", label: "Sinistres", icon: AlertTriangle, feature: "fleet" },
  { path: "/app/fleet/fines", label: "Amendes", icon: Receipt, feature: "fleet" },
  { path: "/app/fleet/trash", label: "Corbeille", icon: Trash2, feature: "fleet" },
  IA_ITEM,
];

const NAV_ACCOUNTING = [
  { path: "/app/accounting", label: "Dossiers mensuels", icon: FolderArchive, feature: "accounting" },
  { path: "/app/accounting/settings", label: "Paramètres", icon: Settings, feature: "accounting" },
  IA_ITEM,
];

const NAV_TASKS = [
  { path: "/app/tasks", label: "Toutes les tâches", icon: CheckSquare, feature: "invoicing" },
  IA_ITEM,
];

// ============================================================================
// UI PRIMITIVES
// ============================================================================

/**
 * NavItem — Ligne cliquable d'une section (sous-item).
 * Feature-flag-aware: si le module n'est pas dans le plan → grisé + lock.
 */
function NavItem({ item, allowedFeatures }) {
  const location = useLocation();
  const isActive = location.pathname === item.path;
  const Icon = item.icon;
  const featureBlocked = item.feature && allowedFeatures && !allowedFeatures.includes(item.feature);

  if (featureBlocked) {
    return (
      <div
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px] text-slate-300 cursor-not-allowed group/item"
        title={`Module non inclus dans votre plan`}
        data-testid={`nav-locked-${item.path.replace(/\//g, "-")}`}
      >
        <div className="w-1 h-1 rounded-full bg-transparent shrink-0" />
        <Lock className="w-3.5 h-3.5 text-slate-300 shrink-0" strokeWidth={1.5} />
        <span className="flex-1 text-left truncate">{item.label}</span>
        <span className="text-[9px] text-slate-300 italic shrink-0">Bientôt</span>
      </div>
    );
  }

  return (
    <NavLink
      to={item.path}
      end={item.end}
      className={({ isActive: active }) =>
        `w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px] transition-all duration-200 group/item ${
          active
            ? "text-blue-700 font-medium bg-blue-50/60"
            : "text-slate-500 hover:text-slate-900 hover:bg-slate-100/50"
        }`
      }
      data-testid={`nav-${item.path.replace(/\//g, "-") || "root"}`}
    >
      {({ isActive: active }) => (
        <>
          <div className={`w-1 h-1 rounded-full transition-all duration-200 shrink-0 ${
            active ? "bg-blue-600 scale-100" : "bg-transparent scale-0 group-hover/item:scale-100 group-hover/item:bg-slate-300"
          }`} />
          <Icon className={`w-4 h-4 transition-colors shrink-0 ${
            active ? "text-blue-600" : "text-slate-400 group-hover/item:text-slate-600"
          }`} strokeWidth={1.5} />
          <span className="flex-1 text-left truncate">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

/**
 * NavSection — Groupe métier rétractable (ex: "Ventes", "RH", "Fleet").
 * Header cliquable avec chevron animé + auto-expand si un item actif dedans.
 */
function NavSection({ title, icon: SectionIcon, items, expanded, onToggle, allowedFeatures }) {
  const location = useLocation();
  const isAnyActive = items.some((item) => location.pathname === item.path);

  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-2 py-2 rounded-lg transition-all duration-200 group ${
          expanded ? "bg-slate-100/80" : "hover:bg-slate-100/50"
        }`}
        data-testid={`section-${title.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <div className="flex items-center gap-2">
          <div className={`p-1 rounded-md transition-colors duration-200 ${
            isAnyActive || expanded
              ? "bg-blue-100 text-blue-600"
              : "bg-slate-100 text-slate-500 group-hover:bg-slate-200"
          }`}>
            <SectionIcon className="w-4 h-4" strokeWidth={2} />
          </div>
          <span className={`text-[12px] font-medium tracking-tight transition-colors truncate ${
            isAnyActive || expanded ? "text-slate-900" : "text-slate-600 group-hover:text-slate-900"
          }`}>
            {title}
          </span>
        </div>
        <ChevronRight
          className={`w-4 h-4 text-slate-400 transition-transform duration-200 shrink-0 ${
            expanded ? "rotate-90" : ""
          }`}
        />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
        expanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
      }`}>
        <div className="pl-3 pr-1 mt-1 space-y-0.5">
          {items.map((item, idx) => (
            <NavItem key={`${item.path}-${idx}`} item={item} allowedFeatures={allowedFeatures} />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * StandaloneNavItem — Item hors section (ex: Assistant IA, Membres, Audit).
 */
function StandaloneNavItem({ path, label, icon: Icon, end, permBlocked }) {
  const location = useLocation();
  const isActive = end
    ? location.pathname === path
    : location.pathname === path || location.pathname.startsWith(`${path}/`);

  if (permBlocked) {
    return null;
  }

  return (
    <NavLink
      to={path}
      end={end}
      className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg transition-all duration-200 group mb-1 ${
        isActive
          ? "bg-blue-50/80 border border-blue-100 shadow-[0_1px_3px_rgba(37,99,235,0.05)]"
          : "hover:bg-slate-100/50"
      }`}
      data-testid={`nav-standalone-${path.replace(/\//g, "-")}`}
    >
      <div className={`p-1 rounded-md transition-colors duration-200 ${
        isActive ? "bg-blue-100 text-blue-600" : "bg-slate-100 text-slate-500 group-hover:bg-slate-200"
      }`}>
        <Icon className="w-4 h-4" strokeWidth={2} />
      </div>
      <span className={`text-[12px] font-medium tracking-tight transition-colors truncate ${
        isActive ? "text-blue-700" : "text-slate-600 group-hover:text-slate-900"
      }`}>
        {label}
      </span>
      {isActive && <ChevronRight className="w-4 h-4 text-blue-500 ml-auto shrink-0" />}
    </NavLink>
  );
}

// ============================================================================
// SIDEBAR CONTENT
// ============================================================================
function SidebarContent({ onNavigate, sections }) {
  const { session, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const allowedFeatures = session?.features || [];

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isDroitSocietesAllowed = allowedFeatures.includes("legal");
  const isAdminOnly = hasPermission("users.read");

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--sidebar-bg))]">
      {/* Header — Logo + tenant name */}
      <div className="flex items-center gap-2.5 h-16 px-3 border-b border-slate-200/60 shrink-0">
        <div className="relative">
          <BrandLogo variant="icon" className="h-9 w-9 rounded-lg" />
          <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-[hsl(var(--sidebar-bg))]" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-sm font-black tracking-tight text-slate-900 truncate"
              style={{ fontFamily: "'Plus Jakarta Sans', Manrope, sans-serif" }}>
            {APP_NAME}
          </h1>
          <p className="text-[10px] font-bold text-slate-400 tracking-wide uppercase truncate">
            {session?.tenant?.name || "SaaS Platform"}
          </p>
        </div>
      </div>

      {/* Navigation — Sections + Standalone items */}
      <div className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5 scrollbar-hide">
        <NavSection title="Pilotage" icon={Zap} items={NAV_PILOTAGE}
          expanded={sections.pilotage} onToggle={() => sections.toggle("pilotage")}
          allowedFeatures={allowedFeatures} />

        <NavSection title="Ventes" icon={TrendingUp} items={NAV_VENTES}
          expanded={sections.ventes} onToggle={() => sections.toggle("ventes")}
          allowedFeatures={allowedFeatures} />

        <NavSection title="Achats" icon={ShoppingBag} items={NAV_ACHATS}
          expanded={sections.achats} onToggle={() => sections.toggle("achats")}
          allowedFeatures={allowedFeatures} />

        <NavSection title="Contrats Clients" icon={FileSignature} items={NAV_CONTRATS}
          expanded={sections.contrats} onToggle={() => sections.toggle("contrats")}
          allowedFeatures={allowedFeatures} />

        <NavSection title="Dépenses avec Engagements" icon={FileCheck} items={NAV_ENGAGEMENTS}
          expanded={sections.engagements} onToggle={() => sections.toggle("engagements")}
          allowedFeatures={allowedFeatures} />

        <div className="h-px bg-slate-200/60 my-3 mx-2" />

        <NavSection title="Trésorerie" icon={Landmark} items={NAV_TREASURY}
          expanded={sections.treasury} onToggle={() => sections.toggle("treasury")}
          allowedFeatures={allowedFeatures} />

        <NavSection title="Comptabilité" icon={FolderArchive} items={NAV_ACCOUNTING}
          expanded={sections.accounting} onToggle={() => sections.toggle("accounting")}
          allowedFeatures={allowedFeatures} />

        <div className="h-px bg-slate-200/60 my-3 mx-2" />

        <NavSection title="Ressources Humaines" icon={UserCircle} items={NAV_RH}
          expanded={sections.hr} onToggle={() => sections.toggle("hr")}
          allowedFeatures={allowedFeatures} />

        <NavSection title="Parc Auto" icon={Car} items={NAV_FLEET}
          expanded={sections.fleet} onToggle={() => sections.toggle("fleet")}
          allowedFeatures={allowedFeatures} />

        {isDroitSocietesAllowed && (
          <StandaloneNavItem path="/app/legal" label="Droit des sociétés" icon={Scale} />
        )}

        <NavSection title="Tâches" icon={CheckSquare} items={NAV_TASKS}
          expanded={sections.tasks} onToggle={() => sections.toggle("tasks")}
          allowedFeatures={allowedFeatures} />

        <StandaloneNavItem path="/app/assistant" label={productLabel("IA")} icon={Sparkles} />

        <div className="h-px bg-slate-200/60 my-3 mx-2" />

        {/* SaaS-specific: administration */}
        <StandaloneNavItem path="/app/pending-actions" label="Actions en attente" icon={Inbox}
          permBlocked={!hasPermission("tenant.read")} />
        <StandaloneNavItem path="/app/members" label="Membres" icon={Users}
          permBlocked={!hasPermission("users.read")} />
        <StandaloneNavItem path="/app/audit" label="Journal d'audit" icon={ScrollText}
          permBlocked={!hasPermission("audit.read")} />
        <StandaloneNavItem path="/app/plan" label="Plan & facturation" icon={CreditCard}
          permBlocked={!hasPermission("tenant.read")} />
        <StandaloneNavItem path="/app/settings/billing" label="Coordonnées légales" icon={Landmark}
          permBlocked={!hasPermission("tenant.update")} />
        <StandaloneNavItem path="/app/settings/connectors" label="Connecteurs" icon={Plug}
          permBlocked={!hasPermission("accounting.read")} />
        <StandaloneNavItem path="/app/settings/notifications" label="Notifications" icon={Bell} />
      </div>

      {/* Footer — User badge + logout */}
      <div className="p-2 mt-auto shrink-0 border-t border-slate-200/60">
        <div className="flex items-center gap-2 p-2 bg-white border border-slate-200/60 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer group">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm shrink-0">
            <span className="text-xs font-bold text-white">
              {(session?.user?.full_name || session?.user?.email || "U").charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              <p className="text-xs font-semibold text-slate-900 truncate">
                {session?.user?.full_name || session?.user?.email?.split("@")[0]}
              </p>
            </div>
            <p className="text-[10px] text-slate-500 truncate">
              {session?.role_label || session?.user?.email}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            className="text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors h-7 w-7"
            title="Déconnexion"
            data-testid="sidebar-logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// RESPONSIVE WRAPPER
// ============================================================================
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  return isMobile;
}

/**
 * Auto-expand a section if the current URL matches one of its items.
 * Prevents the user from having to manually re-expand after nav.
 */
function useAutoExpandedSections() {
  const location = useLocation();
  const path = location.pathname;

  const matches = (prefix) => path === prefix || path.startsWith(prefix + "/");

  const [state, setState] = useState({
    pilotage: path === "/app",
    ventes: matches("/app/invoices") || matches("/app/quotes")
             || matches("/app/analyse-ca") || matches("/app/clients")
             || matches("/app/vigilance"),
    achats: matches("/app/purchases") || matches("/app/suppliers") || matches("/app/expenses"),
    contrats: matches("/app/contracts"),
    engagements: matches("/app/engagements"),
    treasury: matches("/app/treasury"),
    accounting: matches("/app/accounting"),
    hr: matches("/app/hr"),
    fleet: matches("/app/fleet"),
    tasks: matches("/app/tasks"),
  });

  const toggle = (key) => setState((s) => ({ ...s, [key]: !s[key] }));

  // Re-open a section when navigating INTO one of its routes.
  useEffect(() => {
    setState((s) => ({
      ...s,
      ventes: s.ventes || matches("/app/invoices") || matches("/app/quotes")
                       || matches("/app/analyse-ca") || matches("/app/clients")
                       || matches("/app/vigilance"),
      achats: s.achats || matches("/app/purchases") || matches("/app/suppliers")
                       || matches("/app/expenses"),
      contrats: s.contrats || matches("/app/contracts"),
      engagements: s.engagements || matches("/app/engagements"),
      treasury: s.treasury || matches("/app/treasury"),
      accounting: s.accounting || matches("/app/accounting"),
      hr: s.hr || matches("/app/hr"),
      fleet: s.fleet || matches("/app/fleet"),
      tasks: s.tasks || matches("/app/tasks"),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  return { ...state, toggle };
}

export default function Sidebar() {
  const sections = useAutoExpandedSections();
  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  if (!isMobile) {
    return (
      <aside
        className="fixed left-0 top-0 h-screen w-[220px] border-r border-slate-200/60 z-40 shadow-sm"
        data-testid="app-sidebar"
      >
        <SidebarContent sections={sections} />
      </aside>
    );
  }

  return (
    <>
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-[hsl(var(--sidebar-bg))] border-b border-slate-200/60 z-40 flex items-center px-4 gap-3 shadow-sm">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileOpen(true)}
          className="text-slate-600 hover:bg-slate-100"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2 flex-1">
          <BrandLogo variant="icon" className="h-8 w-8 rounded-lg" />
          <span className="font-bold text-slate-900">{APP_NAME}</span>
        </div>
      </header>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-[280px] border-r-0">
          <SheetHeader className="sr-only">
            <SheetTitle>Menu de navigation</SheetTitle>
            <SheetDescription>Accès rapide aux modules de {APP_NAME}.</SheetDescription>
          </SheetHeader>
          <SidebarContent
            sections={sections}
            onNavigate={() => setMobileOpen(false)}
          />
        </SheetContent>
      </Sheet>
    </>
  );
}

export { useIsMobile };
